import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Trash2 } from 'lucide-react';

/**
 * Allocation Dialog for creating/editing resource allocations
 */
const AllocationDialog = ({
  open,
  onOpenChange,
  form,
  setForm,
  employees,
  projects,
  isSubmitting,
  isEditing,
  editingAllocation,
  onSubmit,
  onDelete
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Allokation bearbeiten' : 'Neue Allokation'}
          </DialogTitle>
          <DialogDescription>
            Weisen Sie einen Mitarbeiter einem Projekt zu
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Mitarbeiter *</Label>
            <Select 
              value={form.user_id} 
              onValueChange={(v) => setForm(prev => ({ ...prev, user_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Mitarbeiter wählen..." />
              </SelectTrigger>
              <SelectContent>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Projekt *</Label>
            <Select 
              value={form.project_id} 
              onValueChange={(v) => setForm(prev => ({ ...prev, project_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Projekt wählen..." />
              </SelectTrigger>
              <SelectContent>
                {projects.map(proj => (
                  <SelectItem key={proj.id} value={proj.id}>{proj.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Allokation (%)</Label>
            <Input
              type="number"
              min="0"
              max="200"
              value={form.allocation_percent}
              onChange={(e) => setForm(prev => ({ ...prev, allocation_percent: parseInt(e.target.value) || 0 }))}
            />
            <p className="text-xs text-muted-foreground mt-1">
              100% = Vollzeit, 50% = Halbtags
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Von *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label>Bis *</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                required
              />
            </div>
          </div>

          <div>
            <Label>Rolle</Label>
            <Input
              value={form.role}
              onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))}
              placeholder="z.B. Entwickler, Projektleiter"
            />
          </div>

          <div>
            <Label>Notizen</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
            />
          </div>

          <DialogFooter className="flex justify-between">
            {isEditing && editingAllocation && onDelete && (
              <Button 
                type="button" 
                variant="destructive" 
                onClick={() => onDelete(editingAllocation.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Speichern
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AllocationDialog;
